import { roundToCents } from "@common/helpers/monetary";
import {
    MatchScheduleConflict,
    ScheduleLinkedApplication,
    ScheduleMatchResolution,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import {
    ScheduleMatchResolutionKind,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import {
    ScheduleDocumentLink,
    findDocumentLinkConflicts,
    planDocumentLinks,
} from "./scheduleDocumentLinks";

/** A transaction paying (part of) an invoice; `lineItemIds` empty means the lines are unknown. */
export interface ScheduleMatchLink {
    transactionId: number;
    invoiceId: string;
    lineItemIds: string[];
    amount: number;
}

export type ScheduleMatchApplication = ScheduleLinkedApplication;
export type ScheduleMatchConflict = MatchScheduleConflict;
export type { ScheduleMatchResolution };

export interface ScheduleResolutionStep {
    invoiceId: string;
    invoiceApplications: ScheduleMatchApplication[];
    matchedAmount: number;
    transactions: Array<{
        transactionId: number;
        application: ScheduleMatchApplication;
        amount: number;
        willSplitTransaction: boolean;
    }>;
}

interface PlannableLine {
    lineItemId: string;
    invoiceId: string;
    openAmount: number;
}

function toDocumentLink(link: ScheduleMatchLink): ScheduleDocumentLink {
    const { invoiceId, ...rest } = link;
    return { ...rest, documentId: invoiceId };
}

export function toInvoiceLink(link: ScheduleDocumentLink): ScheduleMatchLink {
    const { documentId, ...rest } = link;
    return { ...rest, invoiceId: documentId };
}

/** {@link planDocumentLinks} for invoice lines. */
export function planMatchLinks(options: {
    transactions: Array<{ transactionId: number; amount: number }>;
    lines: PlannableLine[];
    allocations?: Array<{ lineItemId: string; amount: number }>;
}): ScheduleMatchLink[] {
    return planDocumentLinks({
        ...options,
        lines: options.lines.map(({ invoiceId, ...line }) => ({
            ...line,
            documentId: invoiceId,
        })),
    }).map(toInvoiceLink);
}

/** One conflict per (transaction, invoice) link whose both ends carry a live schedule. */
export function findScheduleMatchConflicts(options: {
    links: ScheduleMatchLink[];
    applications: ScheduleMatchApplication[];
}): ScheduleMatchConflict[] {
    return findDocumentLinkConflicts({
        links: options.links.map(toDocumentLink),
        applications: options.applications,
        documentType: ScheduleSourceType.invoice,
    }).map((conflict) => ({
        transactionId: conflict.link.transactionId,
        invoiceId: conflict.link.documentId,
        lineItemIds: conflict.link.lineItemIds,
        matchedAmount: conflict.link.amount,
        transactionApplication: conflict.transactionApplication,
        invoiceApplications: conflict.documentApplications,
    }));
}

function resolutionTargets(
    resolution: ScheduleMatchResolution,
    conflicts: ScheduleMatchConflict[],
): number[] {
    if (resolution.transactionIds.length > 0) {
        return resolution.transactionIds;
    }
    return conflicts
        .filter((conflict) => conflict.invoiceId === resolution.invoiceId)
        .map((conflict) => conflict.transactionId);
}

/** The conflicts the given resolutions leave unresolved. */
export function coverScheduleConflicts(options: {
    conflicts: ScheduleMatchConflict[];
    resolutions: ScheduleMatchResolution[];
}): ScheduleMatchConflict[] {
    const removedApplicationIds = new Set<string>();
    const unmatchedPairs = new Set<string>();

    for (const resolution of options.resolutions) {
        const targets = resolutionTargets(resolution, options.conflicts);
        for (const conflict of options.conflicts) {
            if (
                conflict.invoiceId !== resolution.invoiceId ||
                !targets.includes(conflict.transactionId)
            ) {
                continue;
            }
            switch (resolution.resolution) {
                case ScheduleMatchResolutionKind.keepInvoice:
                    removedApplicationIds.add(
                        conflict.transactionApplication.applicationId,
                    );
                    break;
                case ScheduleMatchResolutionKind.keepTransactions:
                    for (const application of conflict.invoiceApplications) {
                        removedApplicationIds.add(application.applicationId);
                    }
                    break;
                case ScheduleMatchResolutionKind.unmatch:
                    unmatchedPairs.add(
                        `${conflict.transactionId}:${conflict.invoiceId}`,
                    );
                    break;
                case ScheduleMatchResolutionKind.moveToDocument:
                    break;
            }
        }
    }

    return options.conflicts.flatMap((conflict) => {
        if (
            unmatchedPairs.has(
                `${conflict.transactionId}:${conflict.invoiceId}`,
            ) ||
            removedApplicationIds.has(
                conflict.transactionApplication.applicationId,
            )
        ) {
            return [];
        }
        const invoiceApplications = conflict.invoiceApplications.filter(
            (application) =>
                !removedApplicationIds.has(application.applicationId),
        );
        return invoiceApplications.length === 0
            ? []
            : [{ ...conflict, invoiceApplications }];
    });
}

/** Unmatching one of a transaction's links leaves part of it unmatched, so matching the rest splits it. */
function hasUnmatchedLink(options: {
    transactionId: number;
    conflicts: ScheduleMatchConflict[];
    resolutions: ScheduleMatchResolution[];
}): boolean {
    return options.resolutions.some(
        (resolution) =>
            resolution.resolution === ScheduleMatchResolutionKind.unmatch &&
            resolutionTargets(resolution, options.conflicts).includes(
                options.transactionId,
            ),
    );
}

/** The wizard steps: one per invoice still in conflict, in first-seen order. */
export function buildScheduleResolutionQueue(options: {
    conflicts: ScheduleMatchConflict[];
    resolutions: ScheduleMatchResolution[];
}): ScheduleResolutionStep[] {
    const steps = new Map<string, ScheduleResolutionStep>();
    for (const conflict of coverScheduleConflicts(options)) {
        const step = steps.get(conflict.invoiceId) ?? {
            invoiceId: conflict.invoiceId,
            invoiceApplications: [],
            matchedAmount: 0,
            transactions: [],
        };
        for (const application of conflict.invoiceApplications) {
            if (
                !step.invoiceApplications.some(
                    (existing) =>
                        existing.applicationId === application.applicationId,
                )
            ) {
                step.invoiceApplications.push(application);
            }
        }
        step.matchedAmount = roundToCents(
            step.matchedAmount + conflict.matchedAmount,
        );
        step.transactions.push({
            transactionId: conflict.transactionId,
            application: conflict.transactionApplication,
            amount: conflict.matchedAmount,
            willSplitTransaction:
                (conflict.willSplitTransaction ?? false) ||
                hasUnmatchedLink({
                    transactionId: conflict.transactionId,
                    conflicts: options.conflicts,
                    resolutions: options.resolutions,
                }),
        });
        steps.set(conflict.invoiceId, step);
    }
    return [...steps.values()];
}

/** Whether a resolution of this kind covers the document for the transaction; empty `transactionIds` means every transaction. */
export function isResolutionCovering(options: {
    resolution: ScheduleMatchResolution;
    kind: ScheduleMatchResolutionKind;
    documentId: string;
    transactionId: number;
}): boolean {
    const { resolution } = options;
    return (
        resolution.resolution === options.kind &&
        resolution.invoiceId === options.documentId &&
        (resolution.transactionIds.length === 0 ||
            resolution.transactionIds.includes(options.transactionId))
    );
}

/** Whether the user chose not to match this transaction to this invoice. */
export function isLinkUnmatched(options: {
    link: { transactionId: number; invoiceId: string };
    resolutions: ScheduleMatchResolution[];
}): boolean {
    return options.resolutions.some((resolution) =>
        isResolutionCovering({
            resolution,
            kind: ScheduleMatchResolutionKind.unmatch,
            documentId: options.link.invoiceId,
            transactionId: options.link.transactionId,
        }),
    );
}

/** Links that still get matched once `unmatch` resolutions are honoured. */
export function applyScheduleResolutionsToLinks(options: {
    links: ScheduleMatchLink[];
    resolutions: ScheduleMatchResolution[];
}): ScheduleMatchLink[] {
    return options.links.filter(
        (link) => !isLinkUnmatched({ link, resolutions: options.resolutions }),
    );
}

/** How much of the invoice's schedule the matched transactions do not cover. */
export function getUnscheduledInvoiceAmount(
    step: ScheduleResolutionStep,
): number {
    const scheduled = step.invoiceApplications.reduce(
        (sum, application) => sum + (application.amount ?? 0),
        0,
    );
    return Math.max(0, roundToCents(scheduled - step.matchedAmount));
}

export interface ScheduleTeardownPlan {
    /** Applications the resolutions remove, in resolution order. */
    removedApplicationIds: string[];
    /** Applications the resolutions promise to keep; overlap with removed is a contradiction. */
    keptApplicationIds: string[];
}

/** Which applications the resolutions remove and which they keep. */
export function planScheduleTeardown(options: {
    conflicts: ScheduleMatchConflict[];
    resolutions: ScheduleMatchResolution[];
}): ScheduleTeardownPlan {
    const removed: string[] = [];
    const kept: string[] = [];
    const add = (list: string[], id: string) => {
        if (!list.includes(id)) {
            list.push(id);
        }
    };
    for (const resolution of options.resolutions) {
        const targets = resolutionTargets(resolution, options.conflicts);
        for (const conflict of options.conflicts) {
            if (
                conflict.invoiceId !== resolution.invoiceId ||
                !targets.includes(conflict.transactionId)
            ) {
                continue;
            }
            switch (resolution.resolution) {
                case ScheduleMatchResolutionKind.keepInvoice:
                    add(removed, conflict.transactionApplication.applicationId);
                    for (const application of conflict.invoiceApplications) {
                        add(kept, application.applicationId);
                    }
                    break;
                case ScheduleMatchResolutionKind.keepTransactions:
                    for (const application of conflict.invoiceApplications) {
                        add(removed, application.applicationId);
                    }
                    add(kept, conflict.transactionApplication.applicationId);
                    break;
                case ScheduleMatchResolutionKind.unmatch:
                case ScheduleMatchResolutionKind.moveToDocument:
                    break;
            }
        }
    }
    return { removedApplicationIds: removed, keptApplicationIds: kept };
}
